import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Nav from "@/components/Nav";
import SEO from "@/components/SEO";
import Paginator, { usePagination, SORT } from "@/components/Paginator";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { fmtPrice } from "@/lib/currency";
import { SPORTS } from "@/lib/sports";
import { useSports } from "@/hooks/useSports";
import { MapPin, BadgeCheck, ChevronRight, Calendar, Clock, Sparkles } from "lucide-react";
import VerifiedBadge from "@/components/VerifiedBadge";
import { minTimeForDate, validateFutureDateTime } from "@/lib/dateConstraints";
import DatePicker from "@/components/ui/DatePicker";
import PublicMembershipsList from "@/components/memberships/PublicMembershipsList";

const VENDOR_TYPE_LABEL = {
  ground: "Grounds", court: "Courts", table: "Tables", gym: "Gyms", studio: "Studios",
  coach: "Coaches", referee: "Referees", umpire: "Umpires", trainer: "Trainers",
  photographer: "Photographers", videographer: "Videographers",
};

// Categories that are bookable by sport/location. Gyms and studios also flow
// through the same wizard (pick activity → city → listing → book) so we group
// them with venue types even though they're indoor fitness spaces.
const VENUE_TYPES = ["ground", "court", "table", "gym", "studio"];

// Aliases → vendor_type. When the user types "snooker" / "pool" / "carrom" /
// "table tennis" / "chess" into the search box, we auto-pivot to the `table`
// venue type and pre-select the sport.
const SEARCH_ALIASES = [
  { rx: /\b(snooker|billiard)s?\b/i, type: "table", sport: "snooker" },
  { rx: /\bpool\b/i, type: "table", sport: "pool" },
  { rx: /\b(tt|table[\s-]?tennis|ping[\s-]?pong)\b/i, type: "table", sport: "tabletennis" },
  { rx: /\bcarrom\b/i, type: "table", sport: "carrom" },
  { rx: /\bchess\b/i, type: "table", sport: "chess" },
  { rx: /\btable\b/i, type: "table", sport: "" },
  { rx: /\b(court|badminton|tennis|basketball)\b/i, type: "court", sport: "" },
  { rx: /\b(ground|cricket|football|volleyball)\b/i, type: "ground", sport: "" },
  { rx: /\b(gym|crossfit|yoga|pilates)\b/i, type: "gym", sport: "" },
];

export default function VendorMarket() {
  const { ready, user } = useAuth();
  const nav = useNavigate();

  // Live sport list — includes admin-added sports (e.g. snooker/pool) that
  // aren't in the hardcoded fallback in /lib/sports.js. Filter to ONLY sports
  // that at least one active vendor listing actually offers, so the picker
  // doesn't show empty categories.
  const { sports: allSports } = useSports();
  const [availableSportValues, setAvailableSportValues] = useState(null);
  const dynamicSports = (availableSportValues
    ? allSports.filter((s) => availableSportValues.has(s.value))
    : allSports.length > 0 ? allSports : SPORTS);

  // Dynamic vendor-type chips: only surface a category (Grounds / Courts /
  // Tables / Gyms / Studios / Coaches / …) when at least one approved+active
  // listing of that type exists. Prevents wasted taps on empty buckets — a
  // guest who lands on the hire wizard sees only what's actually bookable.
  const [availableTypes, setAvailableTypes] = useState(null);  // null = still loading

  // Wizard state
  const [vendorType, setVendorType] = useState("ground");
  const [sport, setSport] = useState("");
  const [city, setCity] = useState("");
  const [cities, setCities] = useState([]);
  const [listings, setListings] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ requested_date: "", start_time: "18:00", hours: 2, notes: "", apply_membership_id: "" });

  // Anyone can browse /hire — no login wall. We only gate the click-to-book
  // action so QR-poster traffic and organic visitors can preview listings first.
  // Booking is allowed for native players, HR / organiser accounts, AND any
  // dual-role user who has opted-in as a player (`also_player=true`) — so a
  // vendor / sponsor in player mode can seamlessly book a venue.
  const canBook = !!user && (
    ["player", "organiser", "company_admin"].includes(user.role) || user.also_player === true
  );

  const openBookingFor = (listing) => {
    // Anyone can open the modal to preview the calendar + available slots.
    // We only gate the SUBMIT action inside the modal so anonymous visitors
    // can inspect vacancy before deciding to sign up. Task 5 (Feb 2026).
    setSelected(listing);
  };

  // When sport is picked, fetch cities
  useEffect(() => {
    // Which sports have at least one active vendor listing for the CURRENT
    // vendor_type? Refetches whenever the user switches Grounds ↔ Courts ↔
    // Gyms ↔ Tables etc, so "Gyms" shows fitness activities (yoga / crossfit)
    // and "Tables" shows cue/board sports (snooker / pool / carrom) — never
    // an unrelated sport bleeding across buckets.
    api.get(`/vendor-listings/sports?vendor_type=${vendorType}`).then((r) => {
      const values = new Set(r.data || []);
      setAvailableSportValues(values);
      // If the current `sport` isn't offered under the new vendor_type,
      // clear it so the user isn't stuck on an invalid combination.
      if (sport && !values.has(sport)) setSport("");
    }).catch(() => setAvailableSportValues(null));
  }, [vendorType]);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Which vendor_types have at least one active listing?
    api.get("/vendor-listings/types").then((r) => {
      const set = new Set(r.data || []);
      setAvailableTypes(set);
      // If the default vendorType has no listings, jump to the first available.
      if (set.size > 0 && !set.has("ground")) {
        const firstAvailable = Object.keys(VENDOR_TYPE_LABEL).find((v) => set.has(v));
        if (firstAvailable) setVendorType(firstAvailable);
      }
    }).catch(() => setAvailableTypes(null));
  }, []);

  useEffect(() => {
    if (!sport) { setCities([]); setCity(""); setListings([]); return; }
    api.get(`/vendor-listings/cities?sport=${encodeURIComponent(sport)}&vendor_type=${vendorType}`)
      .then((r) => setCities(r.data))
      .catch(() => {});
    setCity("");
    setListings([]);
  }, [sport, vendorType]);

  // When city is picked, fetch listings
  useEffect(() => {
    if (!(sport && city)) { setListings([]); return; }
    api.get(`/vendor-listings?vendor_type=${vendorType}&sport=${encodeURIComponent(sport)}&city=${encodeURIComponent(city)}`)
      .then((r) => setListings(r.data))
      .catch(() => {});
  }, [sport, city, vendorType]);

  // Paginate + sort listings: newest verified vendor listings on top of page 1.
  const vp = usePagination(listings, { defaultPageSize: 12, sortFn: SORT.byCreatedDesc });

  const step = useMemo(() => {
    if (!sport) return 1;
    if (!city) return 2;
    if (!selected) return 3;
    return 4;
  }, [sport, city, selected]);

  const submitBooking = async () => {
    const err = validateFutureDateTime(form.requested_date, form.start_time);
    if (err) return toast.error(err);
    if (!form.hours || form.hours < 1) return toast.error("Hours must be at least 1");
    // Recurrence — if enabled, expand the base weekday of `requested_date` into
    // a series stretching to `recurrence_until`. Backend does the actual
    // date-list expansion and inserts one booking per occurrence.
    const payload = {
      listing_id: selected.id,
      requested_date: form.requested_date,
      start_time: form.start_time,
      hours: Number(form.hours),
      sport,
      notes: form.notes,
      apply_membership_id: form.apply_membership_id || null,
    };
    if (form.recurrence_enabled && form.recurrence_until) {
      const dow = new Date(form.requested_date + "T00:00:00").getDay(); // 0=Sun..6=Sat (JS)
      const isoDow = (dow + 6) % 7; // Convert to 0=Mon..6=Sun for backend
      payload.recurrence = "weekly";
      payload.recurrence_until = form.recurrence_until;
      payload.recurrence_days_of_week = [isoDow];
    }
    try {
      const { data } = await api.post("/vendor-bookings", payload);
      if (data.recurrence_group_id) {
        toast.success(`Created ${data.count} weekly bookings — cancel or reschedule each occurrence individually from /bookings.`);
      } else {
        toast.success(form.apply_membership_id
          ? "Booked using your membership — vendor will confirm shortly"
          : "Booking request sent — admin will confirm with the vendor");
      }
      setSelected(null);
      nav("/bookings");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  // Surface chips: render the sports list for cricket/football/etc.
  const isVenueFlow = VENUE_TYPES.includes(vendorType);

  return (
    <div className="bg-[#0a0a0a] min-h-screen text-white">
      <SEO
        title="Book a Venue — Turfs, Courts & Coaches | Kreeda Nation"
        description="Book turfs, badminton courts, box cricket and coaches across India. Browse live availability, see verified vendor listings, book in a few clicks."
        canonical="/hire"
      />
      <Nav />
      <div className="max-w-7xl mx-auto px-6 pt-12 pb-24">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#84CC16]">/ Hire</div>
        <h1 className="font-display text-5xl tracking-wide mt-2">BOOK A GROUND OR HIRE TALENT</h1>
        <p className="text-neutral-400 mt-2 text-sm max-w-2xl">Pick a sport and city to see verified Kreeda Nation venues with live rates. Submit a request — admin confirms with the vendor and you&apos;ll see the status update on your bookings page.</p>

        {!user && (
          <div data-testid="vm-guest-banner" className="mt-4 inline-flex items-center gap-2 border border-[#06B6D4]/30 bg-[#06B6D4]/5 rounded-sm px-3 py-2 text-xs text-neutral-300 max-w-2xl">
            <span className="text-[#06B6D4]">👋</span>
            <span>
              Browsing as guest — filter, preview slots, no signup needed. Sign in only when you&rsquo;re ready to book.{" "}
              <Link to="/login" className="text-[#06B6D4] hover:underline">Sign in</Link>
              {" · "}
              <Link to="/register" className="text-[#84CC16] hover:underline">Create account</Link>
            </span>
          </div>
        )}

        {/* Universal search — types like "snooker" or "pool" jump straight
            to Tables → sport pre-selected. Keeps the click-through short. */}
        <div className="mt-8 max-w-md">
          <Label className="text-xs font-mono uppercase text-neutral-500">Looking for something specific?</Label>
          <Input
            data-testid="vm-search"
            placeholder="Try 'snooker', 'pool', 'table tennis', 'badminton court'…"
            className="mt-2 bg-black/40 border-white/10 text-white"
            onChange={(e) => {
              const q = e.target.value.trim();
              if (!q) return;
              for (const alias of SEARCH_ALIASES) {
                if (alias.rx.test(q)) {
                  if (vendorType !== alias.type) {
                    setVendorType(alias.type); setSport(""); setCity(""); setSelected(null);
                  }
                  if (alias.sport) setSport(alias.sport);
                  return;
                }
              }
            }}
          />
        </div>

        {/* Vendor type selector — top bar. Dynamic: only shows buckets with
            at least one active listing so a guest can never end up on an
            empty category. */}
        <div className="mt-6 flex flex-wrap gap-2">
          {Object.entries(VENDOR_TYPE_LABEL)
            .filter(([v]) => availableTypes === null || availableTypes.has(v))
            .map(([v, l]) => (
            <button
              key={v}
              data-testid={`vm-vtype-${v}`}
              onClick={() => { setVendorType(v); setSport(""); setCity(""); setSelected(null); }}
              className={`px-4 py-2 rounded-sm text-xs font-mono uppercase tracking-widest border transition ${
                vendorType === v
                  ? "bg-[#84CC16] text-black border-[#84CC16]"
                  : "bg-[#141414] text-neutral-400 border-white/10 hover:text-white"
              }`}
            >{l}</button>
          ))}
        </div>

        {/* Stepper */}
        <div className="mt-10 flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-neutral-500">
          <StepPill n="1" label="Sport" active={step === 1} done={step > 1} />
          <ChevronRight className="w-3 h-3" />
          <StepPill n="2" label="Location" active={step === 2} done={step > 2} />
          <ChevronRight className="w-3 h-3" />
          <StepPill n="3" label="Pick venue" active={step === 3} done={step > 3} />
          <ChevronRight className="w-3 h-3" />
          <StepPill n="4" label="Date & rate" active={step === 4} done={false} />
        </div>

        {/* Step 1: Sport */}
        {isVenueFlow ? (
          <SectionTitle n="1" title="Pick a sport / surface" />
        ) : (
          <SectionTitle n="1" title="Pick a sport" />
        )}
        <div className="mt-3 flex flex-wrap gap-2">
          {dynamicSports.map((s) => (
            <button
              key={s.value}
              data-testid={`vm-sport-${s.value}`}
              onClick={() => setSport(s.value)}
              className={`px-4 py-2 rounded-sm text-sm border transition ${
                sport === s.value
                  ? "bg-[#06B6D4] text-black border-[#06B6D4] font-semibold"
                  : "bg-[#141414] text-neutral-300 border-white/10 hover:text-white"
              }`}
            >{s.label}</button>
          ))}
        </div>

        {/* Step 2: City */}
        {sport && (
          <>
            <SectionTitle n="2" title="Pick a location" />
            <div className="mt-3 flex flex-wrap gap-2">
              {cities.length === 0 && <span className="text-neutral-500 text-sm">No verified vendors yet for this sport.</span>}
              {cities.map((c) => (
                <button
                  key={c}
                  data-testid={`vm-city-${c}`}
                  onClick={() => setCity(c)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-sm text-sm border transition ${
                    city === c
                      ? "bg-[#EC4899] text-black border-[#EC4899] font-semibold"
                      : "bg-[#141414] text-neutral-300 border-white/10 hover:text-white"
                  }`}
                ><MapPin className="w-3.5 h-3.5" /> {c}</button>
              ))}
            </div>
          </>
        )}

        {/* Step 3: Listings */}
        {sport && city && (
          <>
            <SectionTitle n="3" title={`Available ${VENDOR_TYPE_LABEL[vendorType].toLowerCase()} in ${city}`} />
            <div className="mt-3 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {listings.length === 0 && <div className="col-span-full text-center text-neutral-500 py-10">No verified listings here yet.</div>}
              {vp.view.map((l) => (
                <button
                  key={l.id}
                  data-testid={`vm-listing-${l.id}`}
                  onClick={() => openBookingFor(l)}
                  className="text-left border border-white/10 rounded-sm bg-[#141414] overflow-hidden hover-lift"
                >
                  <div className="h-40 bg-black/40 relative">
                    {l.images?.[0] && <img src={l.images[0]} alt="" className="w-full h-full object-cover" />}
                    <span className="absolute top-2 left-2 text-[10px] font-mono uppercase px-2 py-0.5 rounded-sm bg-black/60 text-white">{l.city}</span>
                    {l.verified && (
                      <span data-testid={`vm-verified-${l.id}`} className="absolute top-2 right-2 inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-sm bg-[#84CC16] text-black font-semibold shadow-[0_2px_8px_rgba(132,204,22,0.35)]">
                        <BadgeCheck className="w-3 h-3" /> Verified
                      </span>
                    )}
                  </div>
                  <div className="p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-semibold">{l.title}</div>
                      {(l.rating_count ?? 0) > 0 && <VerifiedBadge listing={l} />}
                    </div>
                    <div className="text-xs text-neutral-400 mt-1 line-clamp-2">{l.description}</div>
                    {l.cheapest_membership && (
                      <div data-testid={`vm-memb-${l.id}`}
                        className="mt-2 inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-sm bg-[#EC4899]/15 text-[#EC4899] border border-[#EC4899]/40">
                        <Sparkles className="w-3 h-3" /> Membership from {fmtPrice(l.cheapest_membership.price, l.cheapest_membership.currency)}
                      </div>
                    )}
                    <div className="flex items-end justify-between mt-3">
                      <div>
                        <div className="font-mono text-xl text-[#84CC16]">{fmtPrice(l.price, l.currency)}</div>
                        <div className="text-[10px] font-mono uppercase text-neutral-500">{l.price_unit}</div>
                      </div>
                      {l.sports?.length > 0 && <div className="text-[10px] font-mono uppercase text-neutral-400">{l.sports.slice(0, 3).join(" · ")}</div>}
                    </div>
                    <div className="mt-3 pt-2 border-t border-white/5 flex items-center justify-between text-[10px] font-mono uppercase tracking-widest">
                      <button
                        data-testid={`vm-details-${l.id}`}
                        onClick={(e) => { e.stopPropagation(); window.open(`/vendor-listing/${l.id}`, "_blank", "noopener"); }}
                        className="text-[#06B6D4] hover:text-white transition-colors"
                      >
                        View details →
                      </button>
                      <span className="text-neutral-500">Tap card to book</span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
            <Paginator {...vp.controls} label="listings" />
          </>
        )}
      </div>

      {/* Step 4: Booking modal */}
      {selected && (
        <BookingModal
          listing={selected}
          form={form}
          setForm={setForm}
          onSubmit={submitBooking}
          onClose={() => setSelected(null)}
        />
      )}

      <Footer />
    </div>
  );
}

function StepPill({ n, label, active, done }) {
  return (
    <span data-testid={`vm-step-${n}`} className={`px-2 py-1 rounded-sm border ${
      active ? "bg-white/10 text-white border-white/20" : done ? "bg-[#84CC16]/15 text-[#84CC16] border-[#84CC16]/30" : "border-white/10 text-neutral-600"
    }`}>{n}. {label}</span>
  );
}

function SectionTitle({ n, title }) {
  return (
    <div className="mt-10 flex items-end gap-3">
      <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#06B6D4]">/ Step {n}</span>
      <span className="font-display text-2xl tracking-wider">{title.toUpperCase()}</span>
    </div>
  );
}

/**
 * Derive booking-unit metadata from the listing's `price_unit`. This is what
 * lets a Gym listed at "₹2000 per month" render "Months" instead of "Hours"
 * in the wizard — and compute the correct total (price × months).
 *
 * The backend still receives an integer `hours` field (kept for backwards
 * compatibility across bookings), but we relabel the UI so users know they're
 * buying months / sessions / days when relevant.
 */
function unitInfoFor(priceUnit) {
  const u = (priceUnit || "per hour").toLowerCase();
  if (u.includes("month")) return { key: "month", label: "Months", unit: "month",  requiresTime: false, choices: [1, 3, 6, 12] };
  if (u.includes("day"))   return { key: "day",   label: "Days",   unit: "day",    requiresTime: false, choices: [1, 2, 3, 5, 7] };
  if (u.includes("week"))  return { key: "week",  label: "Weeks",  unit: "week",   requiresTime: false, choices: [1, 2, 4, 8] };
  if (u.includes("session") || u.includes("class") || u.includes("match") || u.includes("event") || u.includes("piece") || u.includes("trophy"))
    return { key: "session", label: "Sessions", unit: "session", requiresTime: true, choices: [1, 2, 3, 4, 5] };
  // default → hourly (courts, grounds, tables)
  return { key: "hour", label: "Hours", unit: "hour", requiresTime: true, choices: [1, 2, 3, 4, 5, 6, 8, 10, 12] };
}

/**
 * Given a start date + quantity + unit, compute the access end date for
 * subscription-style listings. Simple calendar math — months add naturally
 * to the same day-of-month (with a JS Date fallback), weeks/days multiply.
 */
function computeAccessEnd(startISO, qty, unit) {
  const d = new Date(startISO + "T00:00:00");
  const n = Math.max(1, Number(qty) || 1);
  if (unit === "month") d.setMonth(d.getMonth() + n);
  else if (unit === "week") d.setDate(d.getDate() + n * 7);
  else if (unit === "day")  d.setDate(d.getDate() + n);
  else                       d.setDate(d.getDate() + n);  // safe default
  // -1 day so the end date is inclusive (e.g. 20 Feb + 1 month → 19 Mar)
  d.setDate(d.getDate() - 1);
  return d;
}

export function BookingModal({ listing, form, setForm, onSubmit, onClose }) {
  const { user } = useAuth();
  // Booking is open to native players, HR / organiser, and any dual-role
  // user who's opted-in (also_player=true) — so a vendor / sponsor in player
  // mode isn't blocked by a "Sign in to book" wall.
  const canBook = !!user && (
    ["player", "organiser", "company_admin"].includes(user.role) || user.also_player === true
  );
  const unitInfo = useMemo(() => unitInfoFor(listing.price_unit), [listing.price_unit]);
  const total = useMemo(() => Number(listing.price) * Number(form.hours || 0), [listing.price, form.hours]);
  const [availability, setAvailability] = useState(null);
  const [eligibility, setEligibility] = useState(null);

  useEffect(() => {
    if (!form.requested_date) { setAvailability(null); return; }
    let cancelled = false;
    api.get(`/vendor-listings/${listing.id}/availability?date=${form.requested_date}`)
      .then((r) => { if (!cancelled) setAvailability(r.data); })
      .catch(() => { if (!cancelled) setAvailability(null); });
    return () => { cancelled = true; };
  }, [form.requested_date, listing.id]);

  // Fetch buyer's eligible membership for this listing once on open
  useEffect(() => {
    let cancelled = false;
    api.get(`/memberships/my-eligibility?listing_id=${listing.id}`)
      .then((r) => { if (!cancelled) setEligibility(r.data?.eligible || null); })
      .catch(() => { if (!cancelled) setEligibility(null); });
    return () => { cancelled = true; };
  }, [listing.id]);

  const pickSlot = (s) => {
    if (s.status !== "available") return;
    setForm({ ...form, start_time: s.time });
  };

  const applyOn = !!form.apply_membership_id;
  const displayTotal = applyOn ? 0 : total;

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-start justify-center overflow-auto p-6" onClick={onClose}>
      <div className="bg-[#0c0c0c] border border-white/10 rounded-sm w-full max-w-2xl my-10 text-white" onClick={(e) => e.stopPropagation()}>
        <div className="aspect-video bg-black/40 relative">
          {listing.images?.[0] && <img src={listing.images[0]} alt="" className="w-full h-full object-cover" />}
        </div>
        <div className="p-6 space-y-4">
          <div>
            <div className="font-display text-3xl tracking-wider flex items-center gap-3 flex-wrap">
              {listing.title}
              <VerifiedBadge listing={listing} size="lg" />
            </div>
            <div className="text-xs font-mono text-neutral-500 uppercase mt-1">{listing.city} · {listing.sports?.join(" · ") || ""}</div>
            <p className="text-sm text-neutral-300 mt-2">{listing.description}</p>
            {listing.cheapest_membership && (
              <div data-testid="vm-detail-memb"
                className="mt-3 border border-[#EC4899]/40 bg-gradient-to-r from-[#EC4899]/10 to-transparent rounded-sm p-3 flex items-center gap-3">
                <Sparkles className="w-5 h-5 text-[#EC4899] shrink-0" />
                <div className="text-sm">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#EC4899]">/ Recommended membership</div>
                  <div className="text-neutral-200">
                    Save vs hourly rates — memberships from
                    <span className="text-[#EC4899] font-semibold ml-1">{fmtPrice(listing.cheapest_membership.price, listing.cheapest_membership.currency)}</span>.
                  </div>
                </div>
              </div>
            )}
            <PublicMembershipsList listingId={listing.id} title="Buy a membership for this venue" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs font-mono uppercase text-neutral-500 flex items-center gap-1"><Calendar className="w-3 h-3" />Date *</Label>
              <Input data-testid="vm-book-date-fallback" type="hidden" value={form.requested_date} readOnly />
              <DatePicker
                testid="vm-book-date"
                value={form.requested_date}
                onChange={(v) => setForm({ ...form, requested_date: v })}
                placeholder="Pick a date"
                className="mt-2"
              />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase text-neutral-500 flex items-center gap-1"><Clock className="w-3 h-3" />{unitInfo.requiresTime ? "Start" : "Start time (n/a)"}</Label>
              <Input data-testid="vm-book-start" type="time" min={unitInfo.requiresTime ? minTimeForDate(form.requested_date) : undefined}
                     disabled={!unitInfo.requiresTime}
                     value={unitInfo.requiresTime ? form.start_time : "00:00"}
                     onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                     className="mt-2 bg-black/40 border-white/10 text-white disabled:opacity-40" />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase text-neutral-500">{unitInfo.label} *</Label>
              <Select value={String(form.hours)} onValueChange={(v) => setForm({ ...form, hours: Number(v) })}>
                <SelectTrigger data-testid="vm-book-hours" className="mt-2 bg-black/40 border-white/10 text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#141414] text-white border-white/10">
                  {unitInfo.choices.map((n) => (
                    <SelectItem key={n} value={String(n)}>{n} {unitInfo.unit}{n > 1 ? "s" : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* SLOT GRID — only meaningful for hourly listings */}
          {availability && unitInfo.requiresTime && (
            <div data-testid="vm-slot-grid">
              <Label className="text-xs font-mono uppercase text-neutral-500">Available slots {availability.is_weekend && <span className="ml-2 text-[#F59E0B]">· weekend pricing</span>}</Label>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {availability.slots.map((s) => (
                  <button
                    key={s.time}
                    data-testid={`vm-slot-${s.time.replace(":", "")}`}
                    onClick={() => pickSlot(s)}
                    disabled={s.status !== "available"}
                    title={`${fmtPrice(s.price, availability.currency)} / hr · ${s.status}`}
                    className={`text-[11px] font-mono px-2 py-1 rounded-sm border transition ${
                      form.start_time === s.time
                        ? "bg-[#06B6D4] text-black border-[#06B6D4] font-semibold"
                        : s.status === "available"
                        ? "bg-[#84CC16]/10 text-[#84CC16] border-[#84CC16]/30 hover:bg-[#84CC16]/20"
                        : s.status === "booked"
                        ? "bg-[#FF3B30]/10 text-[#FF3B30]/60 border-[#FF3B30]/30 cursor-not-allowed"
                        : "bg-neutral-900 text-neutral-600 border-white/10 cursor-not-allowed"
                    }`}>
                    {s.time}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <Label className="text-xs font-mono uppercase text-neutral-500">Notes</Label>
            <Textarea data-testid="vm-book-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Tournament name, slot preference, etc." className="mt-2 bg-black/40 border-white/10 text-white" />
          </div>

          {/* Weekly recurrence — creates individual bookings per week so each
              occurrence can be rescheduled or cancelled on its own. Only
              meaningful for hourly / per-session bookings; hidden for monthly
              / daily / weekly subscription-style listings. */}
          {unitInfo.requiresTime && (
          <label data-testid="vm-recurrence-toggle-label" className="flex items-start gap-2 border border-[#06B6D4]/40 bg-[#06B6D4]/5 rounded-sm p-3 cursor-pointer">
            <input
              type="checkbox"
              data-testid="vm-recurrence-toggle"
              checked={!!form.recurrence_enabled}
              onChange={(e) => setForm({ ...form, recurrence_enabled: e.target.checked })}
              className="mt-0.5 accent-[#06B6D4]"
            />
            <div className="flex-1">
              <div className="text-sm font-semibold">Book this weekly</div>
              <div className="text-[11px] font-mono text-neutral-400 mt-0.5">Every {form.requested_date ? new Date(form.requested_date + "T00:00:00").toLocaleDateString(undefined, { weekday: "long" }) : "matching weekday"} until…</div>
              {form.recurrence_enabled && (
                <div className="mt-2 flex items-center gap-2">
                  <Input data-testid="vm-recurrence-until" type="date" value={form.recurrence_until || ""} min={form.requested_date || undefined} onChange={(e) => setForm({ ...form, recurrence_until: e.target.value })} className="bg-black/40 border-white/10 text-white h-8 text-xs w-44" />
                  <span className="text-[10px] font-mono text-neutral-500">Each week becomes its own booking so you can cancel or reschedule them individually.</span>
                </div>
              )}
            </div>
          </label>
          )}

          {/* Access range readout — visible only for subscription-style units
              (month / week / day) so users know exactly when their plan
              begins and expires before they commit. */}
          {!unitInfo.requiresTime && form.requested_date && Number(form.hours) > 0 && (
            <div data-testid="vm-access-range" className="border border-[#84CC16]/30 bg-[#84CC16]/5 rounded-sm p-3 text-xs font-mono">
              <div className="text-[10px] uppercase tracking-widest text-[#84CC16] mb-1">/ Access period</div>
              <div className="text-neutral-200">
                <span className="text-white">{new Date(form.requested_date + "T00:00:00").toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" })}</span>
                <span className="text-neutral-500 mx-2">→</span>
                <span className="text-white">{computeAccessEnd(form.requested_date, form.hours, unitInfo.unit).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" })}</span>
                <span className="text-neutral-500 ml-2">({Number(form.hours)} {unitInfo.unit}{Number(form.hours) > 1 ? "s" : ""})</span>
              </div>
            </div>
          )}

          {eligibility && (
            <label data-testid="vm-apply-memb" className="flex items-start gap-2 border border-[#EC4899]/40 bg-[#EC4899]/5 rounded-sm p-3 cursor-pointer">
              <input
                type="checkbox"
                data-testid="vm-apply-memb-toggle"
                checked={applyOn}
                onChange={(e) => setForm({ ...form, apply_membership_id: e.target.checked ? eligibility.purchase_id : "" })}
                className="mt-0.5 accent-[#EC4899]"
              />
              <div className="min-w-0">
                <div className="text-sm font-semibold flex items-center gap-2 flex-wrap">
                  Apply my <span className="text-[#EC4899]">{eligibility.plan_title}</span> membership
                </div>
                <div className="text-[11px] font-mono text-neutral-400 mt-0.5">
                  {eligibility.bookings_allowed != null
                    ? `${eligibility.bookings_used}/${eligibility.bookings_allowed} bookings used · ${eligibility.bookings_remaining} left`
                    : "Unlimited bookings"}
                  {eligibility.expires_at && ` · expires ${eligibility.expires_at.slice(0, 10)}`}
                </div>
                {applyOn && (
                  <div className="text-[11px] text-[#84CC16] mt-1">This slot is free under your membership.</div>
                )}
              </div>
            </label>
          )}

          <div className="border-t border-white/10 pt-4 flex items-end justify-between">
            <div>
              <div className="text-[10px] font-mono uppercase text-neutral-500">Rate · {listing.price_unit}</div>
              <div className="font-mono text-base text-neutral-300">{fmtPrice(listing.price, listing.currency)}</div>
              <div className="text-[10px] font-mono uppercase text-neutral-500 mt-1">Estimated total ({form.hours || 0} {unitInfo.unit}{(form.hours || 0) > 1 ? "s" : ""}){applyOn && <span className="text-[#EC4899] ml-1">· via membership</span>}</div>
              <div data-testid="vm-book-total" className={`font-display text-3xl ${applyOn ? "text-[#EC4899]" : "text-[#84CC16]"}`}>{fmtPrice(displayTotal, listing.currency)}</div>
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={onClose} className="text-neutral-400">Cancel</Button>
              {canBook ? (
                <Button data-testid="vm-book-submit" onClick={onSubmit} className="bg-[#84CC16] hover:bg-[#65A30D] text-black font-semibold rounded-sm">Send request</Button>
              ) : (
                <a
                  href={`/login?next=${encodeURIComponent("/hire")}`}
                  data-testid="vm-book-login-cta"
                  className="inline-flex items-center gap-2 bg-[#84CC16] hover:bg-[#65A30D] text-black font-semibold rounded-sm px-4 py-2 text-sm"
                >
                  Sign in to book
                </a>
              )}
            </div>
          </div>
          {!canBook && (
            <p data-testid="vm-book-anon-hint" className="text-[10px] font-mono text-neutral-500 mt-2 text-right">
              Browse freely — <a href={`/signup-company`} className="text-[#84CC16] hover:underline">create HR</a>, <a href={`/signup-organiser`} className="text-[#06B6D4] hover:underline">organiser</a>, or <a href={`/players/signup`} className="text-[#84CC16] hover:underline">player</a> account to book this slot.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
